import React, { useState, useEffect, useRef } from 'react';
import SectionContainer from '../../components/UI/SectionContainer';
import Spinner from '../../components/UI/Spinner';
import ErrorMessage from '../../components/UI/ErrorMessage';
import { openAlert } from '../../utils/common';
import axiosInstance from '../../utils/axios';

const MAX_FILE_COUNT = 1;
const MAX_SIZE_MB = 50; // 펌웨어 파일 최대 크기

function FirmwareUploadPage() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const fileInputRef = useRef();

  useEffect(() => {
    fetchFirmware();
  }, []);

  const fetchFirmware = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/file/firmware/latest');
      
      if (response.data.success && response.data.data) {
        setFiles([response.data.data]);
      } else {
        setFiles([]);
      }
    } catch (e) {
      const errorMessage = e.response?.data?.message || e.message || '펌웨어 정보 조회 중 오류가 발생했습니다.';
      openAlert({ title: '알림', description: errorMessage });
      setFiles([]);
    }
    setLoading(false);
  };

  // 파일 검증 함수
  const validateFile = (file) => {
    const errors = {};
    
    // 파일 크기 검증
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      errors.size = `파일 크기는 ${MAX_SIZE_MB}MB 이하여야 합니다.`;
    }
    
    // 파일 확장자 검증 (펌웨어 앱 이미지 — OTA용 .sfb만)
    const allowedExtensions = ['.sfb'];
    const fileName = file.name.toLowerCase();
    const hasValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));
    
    if (!hasValidExtension) {
      errors.type = `허용된 펌웨어 파일 형식: ${allowedExtensions.join(', ')}`;
    }
    
    // 파일명 검증 (특수문자 제한)
    const invalidChars = /[<>:"/\\|?*]/;
    if (invalidChars.test(file.name)) {
      errors.name = '파일명에 특수문자(<>:"/\\|?*)를 포함할 수 없습니다.';
    }
    
    return errors;
  };

  const handleFileChange = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    
    // 파일 개수 제한 확인
    if (files.length >= MAX_FILE_COUNT) {
      setValidationErrors({ count: `펌웨어 파일은 ${MAX_FILE_COUNT}개까지 업로드할 수 있습니다.` });
      return;
    }
    
    // 파일 검증 실행
    const fileErrors = validateFile(f);
    if (Object.keys(fileErrors).length > 0) {
      setValidationErrors(fileErrors);
      return;
    }
    
    // 검증 통과 시 오류 메시지 제거
    setValidationErrors({});
    
    // 파일을 상태에 추가 (실제 업로드는 handleUpload에서 수행)
    setFiles((prev) => [...prev, { file: f, uploadedName: f.name }]);
  };

  const handleDeleteFile = (uploadedName) => {
    setFiles(files.filter((f) => (f.uploadedName || f.fileName) !== uploadedName));
    setValidationErrors({}); // 파일 삭제 시 오류 메시지 제거
  };

  const handleDeleteAll = () => {
    setFiles([]);
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      openAlert({ title: '알림', description: '파일을 선택해주세요.' });
      return;
    }
    if (!window.confirm('펌웨어 파일을 업데이트하시겠습니까?')) return;
    setLoading(true);
    
    const formData = new FormData();
    files.forEach((fileInfo) => {
      formData.append('file_array[]', fileInfo.file);
    });
    
    try {
      const response = await axiosInstance.post('/file/upload/firmware', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.success) {
        openAlert({ title: '알림', description: '펌웨어 업로드가 완료되었습니다.' });
        window.location.reload();
      } else {
        const errorMessage = response.data.message || '업로드 중 오류가 발생했습니다.';
        openAlert({ title: '알림', description: errorMessage });
      }
    } catch (e) {
      const errorMessage = e.response?.data?.message || e.message || '업로드 중 오류가 발생했습니다.';
      openAlert({ title: '알림', description: errorMessage });
    }
    setLoading(false);
  };

  return (
    <SectionContainer title="펌웨어 파일 업로드" className="popup_edit notice_edit edit write">
      <Spinner visible={loading} />
      <div className="content_area">
        <div className="notice">
          <div className="notice_area">
            <h3 className="mb-1 tit-h3">파일 업로드</h3>
            <div className="file-upload-wrap">
              <div className="file-upload mr-1 file-upload-main" style={{ overflow: 'hidden' }}>
                <p>펌웨어 파일(.sfb)을 선택해주세요. (최대 50MB)</p>
                <input
                  type="file"
                  accept=".sfb"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                />
                <button className="btn btn-md primary mt-2" onClick={() => fileInputRef.current.click()}>
                  파일선택
                </button>
                
                {/* 검증 오류 메시지 표시 */}
                {Object.keys(validationErrors).length > 0 && (
                  <div className="mt-2">
                    {Object.entries(validationErrors).map(([key, message]) => (
                      <ErrorMessage key={key} message={message} />
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="file-upload-result mt-2">
              <div className="upload-top">
                <div className="file-total"><span className="current" id="fileCount">{files.length}개</span> / {MAX_FILE_COUNT}개</div>
                <button type="button" style={{ display: files.length === 0 ? 'none' : 'block' }} onClick={handleDeleteAll} className="btn btn-txt ico-before ico-del sm h-auto" id="allDeleteButton">전체 파일 삭제</button>
              </div>
              <ul className="list-group mt-3">
                {files.map((f, idx) => (
                  <li className="list-group-item d-flex justify-content-between align-items-center" key={idx}>
                    <div className="firmware-info">
                      <div className="firmware-name">
                        {f.uploadedName || f.fileName?.split('/').pop() || f.file?.name}
                      </div>
                      {(f.version || f.file_byte) && (
                        <div className="firmware-details text-muted small">
                          {f.version && <span>버전: {f.version}</span>}
                          {f.version && f.file_byte && <span> | </span>}
                          {f.file_byte && <span>크기: {(f.file_byte / 1024 / 1024).toFixed(2)} MB</span>}
                        </div>
                      )}
                    </div>
                    <button className="btn btn-sm btn-danger" onClick={() => handleDeleteFile(f.uploadedName || f.fileName)}>
                      삭제
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="button_wrap">
            <div className="but-flex">
              <input type="button" className="btn btn-md primary" onClick={handleUpload} disabled={loading || files.length === 0} value="업로드"/>
            </div>
          </div>
        </div>
      </div>
    </SectionContainer>
    );
}

export default FirmwareUploadPage;
